package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import com.merkost.handpass.admin.LocalThemeMode
import com.merkost.handpass.admin.auth.AuthState
import com.merkost.handpass.admin.auth.LocalThemeSetter
import com.merkost.handpass.admin.container.SidebarContainer
import com.merkost.handpass.admin.contract.EnvLabel
import com.merkost.handpass.admin.contract.SidebarIntent
import com.merkost.handpass.admin.di.koinContainer
import com.merkost.handpass.admin.util.LocalViewportMode
import com.merkost.handpass.admin.util.ViewportMode
import com.merkost.handpass.sharedweb.components.WebIcon
import com.merkost.handpass.sharedweb.components.Icon
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.AccentRing
import com.merkost.handpass.sharedweb.theme.AccentSoft
import com.merkost.handpass.sharedweb.theme.AnimNames
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.Danger
import com.merkost.handpass.sharedweb.theme.GapDefault
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.OnAccent
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.Pad
import com.merkost.handpass.sharedweb.theme.RadiusButton
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.WebType
import com.merkost.handpass.sharedweb.theme.WebTransitions
import com.merkost.handpass.sharedweb.theme.transitionColors
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedBy
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement
import pro.respawn.flowmvi.compose.dsl.subscribe

data class NavItem(val label: String, val path: String, val showsBadge: Boolean = false, val icon: WebIcon = WebIcon.Queue)

data class NavGroup(val label: String, val items: List<NavItem>)

private val navGroups = listOf(
    NavGroup(
        label = "Moderation",
        items = listOf(
            NavItem("Queue", "/", showsBadge = true, icon = WebIcon.Queue),
            NavItem("Users", "/users", icon = WebIcon.Users),
            NavItem("Listings", "/listings", icon = WebIcon.Listings),
            NavItem("Image Review", "/images", icon = WebIcon.Eye),
        ),
    ),
    NavGroup(
        label = "Content",
        items = listOf(
            NavItem("Partner Listings", "/partner-listings", icon = WebIcon.Listings),
        ),
    ),
    NavGroup(
        label = "Insights",
        items = listOf(
            NavItem("Logs", "/logs", icon = WebIcon.Logs),
            NavItem("Stats", "/stats", icon = WebIcon.Stats),
        ),
    ),
    NavGroup(
        label = "System",
        items = listOf(
            NavItem("Feature Flags", "/feature-flags", icon = WebIcon.Check),
        ),
    ),
)

val AdminNavItemStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(11.px)
            .padding(9.px, 12.px)
            .borderRadius(RadiusControl.value())
            .color(OnSurfaceMuted.value())
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .transitionColors()
    }
    hover {
        Modifier
            .backgroundColor(SurfaceMuted.value())
            .color(OnSurface.value())
    }
    focusVisible {
        Modifier
            .outline(0.px, LineStyle.None, Color("transparent"))
            .boxShadow(spreadRadius = 3.px, color = AccentRing.value())
    }
}

val AdminNavItemActiveStyle = AdminNavItemStyle.extendedBy {
    base {
        Modifier
            .backgroundColor(AccentSoft.value())
            .color(Accent.value())
            .fontWeight(600)
    }
    hover {
        Modifier
            .backgroundColor(AccentSoft.value())
            .color(Accent.value())
    }
}

@Composable
fun Sidebar(
    currentPath: String,
    authState: AuthState.LoggedIn,
    onNavigate: (String) -> Unit,
    onLogout: () -> Unit,
    embedded: Boolean = false,
) {
    val container = koinContainer<SidebarContainer>()
    LaunchedEffect(currentPath) { container.store.intent(SidebarIntent.SetCurrentPath(currentPath)) }
    LaunchedEffect(authState) { container.store.intent(SidebarIntent.SetAuth(authState)) }

    val state by container.store.subscribe { _ -> }
    val viewportMode = LocalViewportMode.current

    val sidebarWidthPx = when (viewportMode) {
        ViewportMode.Wide -> 248
        ViewportMode.Compact -> 64
        ViewportMode.Mobile -> 248
    }
    val showLabels = viewportMode != ViewportMode.Compact

    Div({
        style {
            if (embedded) {
                width(100.percent)
                minHeight(100.vh)
                property("background-color", Surface.value().toString())
                property("display", "flex")
                flexDirection(FlexDirection.Column)
                property("position", "relative")
            } else {
                width(sidebarWidthPx.px)
                minHeight(100.vh)
                property("background-color", Surface.value().toString())
                property("border-right", "1px solid ${Border.value()}")
                property("display", "flex")
                flexDirection(FlexDirection.Column)
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("bottom", "0")
                property("z-index", "10")
            }
        }
    }) {
        SidebarHeader(envLabel = state.envLabel, showLabels = showLabels)
        SidebarNav(groups = navGroups, currentPath = state.currentPath, pendingCount = state.pendingCount, showLabels = showLabels, onNavigate = onNavigate)
        SidebarFooter(authState = authState, showLabels = showLabels, onLogout = onLogout)
    }
}

@Composable
private fun SidebarHeader(envLabel: EnvLabel, showLabels: Boolean) {
    Div({
        style {
            property("padding", "${Pad.value()} ${Pad.value()} ${GapDefault.value()}")
        }
    }) {
        if (showLabels) {
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "10px")
                    property("margin-bottom", "10px")
                }
            }) {
                BrandLogo(size = 30)
                H2({
                    style {
                        property("font-size", "${WebType.Title}")
                        property("font-weight", "700")
                        property("color", OnSurface.value().toString())
                        property("margin", "0")
                        property("letter-spacing", "-0.01em")
                    }
                }) { Text("Handpass Admin") }
            }
            if (envLabel != EnvLabel.PROD) {
                AdminBadge(label = envLabel.name, variant = AdminBadgeVariant.Warning)
            }
        } else {
            Div({
                style {
                    property("display", "flex")
                    property("justify-content", "center")
                    if (envLabel != EnvLabel.PROD) property("margin-bottom", "${HandpassSpacing.SM}")
                }
            }) { BrandLogo(size = 30) }
            if (envLabel != EnvLabel.PROD) {
                AdminBadge(label = envLabel.name, variant = AdminBadgeVariant.Warning)
            }
        }
    }
}

@Composable
private fun BrandLogo(size: Int) {
    Img(src = "/logo.svg", attrs = {
        attr("alt", "Handpass")
        attr("width", size.toString())
        attr("height", size.toString())
        style {
            width(size.px)
            height(size.px)
            property("flex-shrink", "0")
            property("border-radius", "${RadiusControl.value()}")
        }
    })
}

@Composable
private fun SidebarNav(groups: List<NavGroup>, currentPath: String, pendingCount: Int, showLabels: Boolean, onNavigate: (String) -> Unit) {
    Nav({
        style {
            property("flex", "1")
            property("padding", "8px 10px")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "2px")
        }
    }) {
        groups.forEach { group ->
            if (showLabels) NavGroupLabel(label = group.label)
            group.items.forEach { item ->
                NavRow(
                    item = item,
                    isActive = currentPath == item.path || (item.path != "/" && currentPath.startsWith(item.path)),
                    pendingCount = pendingCount,
                    showLabels = showLabels,
                    onNavigate = onNavigate,
                )
            }
        }
    }
}

@Composable
private fun NavGroupLabel(label: String) {
    Div({
        style {
            property("font-size", "${WebType.Nav}")
            property("font-weight", "600")
            property("letter-spacing", "0.07em")
            property("text-transform", "uppercase")
            property("color", OnSurfaceFaint.value().toString())
            property("padding", "12px 12px 6px")
        }
    }) { Text(label) }
}

@Composable
private fun NavRow(item: NavItem, isActive: Boolean, pendingCount: Int, showLabels: Boolean, onNavigate: (String) -> Unit) {
    val itemStyle = if (isActive) AdminNavItemActiveStyle else AdminNavItemStyle
    if (showLabels) {
        Div(
            attrs = itemStyle.toAttrs {
                onClick { onNavigate(item.path) }
                style { property("justify-content", "space-between") }
            }
        ) {
            if (isActive) ActiveAccentBar()
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "11px")
                }
            }) {
                Icon(item.icon, size = 18)
                Span { Text(item.label) }
            }
            if (item.showsBadge && pendingCount > 0) {
                NavCountPill(count = pendingCount)
            }
        }
    } else {
        Button(
            attrs = itemStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                onClick { onNavigate(item.path) }
                attr("aria-label", item.label)
                style {
                    property("justify-content", "center")
                    property("background", "transparent")
                    property("border", "none")
                    property("font", "inherit")
                    width(100.percent)
                }
            }
        ) {
            if (isActive) ActiveAccentBar()
            Icon(item.icon, size = 18)
        }
    }
}

@Composable
private fun ActiveAccentBar() {
    Div({
        style {
            width(3.px)
            height(18.px)
            property("background-color", Accent.value().toString())
            property("border-radius", "0 3px 3px 0")
            property("position", "absolute")
            property("left", "-10px")
            property("top", "50%")
            property("transform", "translateY(-50%)")
            property("transform-origin", "center")
            property("animation", "${AnimNames.AccentBarIn} ${WebTransitions.Normal}")
        }
    }) {}
}

@Composable
private fun NavCountPill(count: Int) {
    Span({
        style {
            property("background-color", Danger.value().toString())
            property("color", OnAccent.value().toString())
            property("border-radius", "${RadiusButton.value()}")
            property("font-weight", "700")
            property("font-size", "${WebType.Micro}")
            property("min-width", "19px")
            property("height", "19px")
            property("padding", "0 6px")
            property("display", "grid")
            property("place-items", "center")
            property("margin-left", "auto")
        }
    }) { Text("$count") }
}

@Composable
private fun SidebarFooter(authState: AuthState.LoggedIn, showLabels: Boolean, onLogout: () -> Unit) {
    val themeSetter = LocalThemeSetter.current
    val currentTheme = LocalThemeMode.current

    Div({
        style {
            property("padding", "${Pad.value()}")
            property("border-top", "1px solid ${Border.value()}")
        }
    }) {
        if (showLabels) {
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "10px")
                    property("margin-bottom", "12px")
                }
            }) {
                AdminAvatar(name = authState.session.email, url = null, size = AdminAvatarSize.MD)
                Div({ style { property("min-width", "0") } }) {
                    Div({
                        style {
                            property("font-size", "${WebType.Label}")
                            property("font-weight", "600")
                            property("color", OnSurface.value().toString())
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                    }) { Text(authState.session.email) }
                    Div({
                        style {
                            property("font-size", "${WebType.Nav}")
                            property("color", OnSurfaceFaint.value().toString())
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.05em")
                        }
                    }) { Text(authState.role.name) }
                }
            }
            AdminThemeControl(current = currentTheme, compact = false, onSelect = themeSetter)
            Div({ style { height(HandpassSpacing.XS) } })
        } else {
            AdminThemeControl(current = currentTheme, compact = true, onSelect = themeSetter)
            Div({ style { height(HandpassSpacing.XS) } })
        }
        AdminButton(label = "Sign Out", onClick = onLogout, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
    }
}
